import { useEffect, useState } from "react";
import { api, can } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", urgent: false });
  const canPost = can(user.role, "founder", "admin", "officer");
  const canDelete = can(user.role, "founder", "admin");

  const load = () => api.get("/announcements").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title:"", content:"", urgent:false }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, content: a.content, urgent: a.urgent }); setOpen(true); };

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Tüm alanlar gerekli");
    try {
      if (editing) await api.put(`/announcements/${editing.id}`, form);
      else await api.post("/announcements", form);
      toast.success(editing ? "Duyuru güncellendi" : "Duyuru yayınlandı");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`"${a.title}" silinsin mi?`)) return;
    try { await api.delete(`/announcements/${a.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-5xl">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// BROADCAST</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Duyurular</h1>
          <p className="text-zinc-500 mt-2">Ekip haberleri, acil bildirimler ve etkinlik duyuruları.</p>
        </div>
        {canPost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-announcement-button" className="bg-rose-600 hover:bg-rose-700">
                <Plus className="w-4 h-4 mr-2" /> Duyuru Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Duyuruyu Düzenle" : "Yeni Duyuru"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs font-mono">BAŞLIK</Label>
                  <Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="announcement-title-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs font-mono">İÇERİK</Label>
                  <Textarea rows={6} value={form.content} onChange={e=>setForm({...form,content:e.target.value})} data-testid="announcement-content-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div className="flex items-center gap-2">
                  <Checkbox id="urgent" checked={form.urgent} onCheckedChange={v=>setForm({...form,urgent:!!v})} data-testid="announcement-urgent-checkbox" />
                  <Label htmlFor="urgent" className="text-sm cursor-pointer">Acil duyuru olarak işaretle (tüm üyelere bildirim)</Label>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="announcement-submit-button" className="bg-rose-600 hover:bg-rose-700">Yayınla</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="space-y-3">
        {items.length === 0 && (
          <Card className="bg-zinc-950 border-zinc-900 p-10 text-center">
            <Megaphone className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-500 text-sm">Henüz duyuru yok.</p>
          </Card>
        )}
        {items.map(a => (
          <Card key={a.id} className={`bg-zinc-950 border-zinc-900 p-6 ${a.urgent ? "border-l-4 border-l-rose-500" : ""}`} data-testid={`announcement-${a.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {a.urgent && <span className="text-[10px] font-mono bg-rose-600/20 text-rose-400 px-2 py-0.5 border border-rose-600/40">ACİL</span>}
                  <span className="text-[10px] font-mono text-zinc-500 tracking-wider">{new Date(a.created_at).toLocaleString("tr-TR")}</span>
                  <span className="text-[10px] font-mono text-zinc-500">// {a.author_name}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{a.title}</h3>
                <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">{a.content}</p>
              </div>
              {canPost && (
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="ghost" onClick={()=>openEdit(a)} data-testid={`edit-announcement-${a.id}`}><Edit className="w-4 h-4" /></Button>
                  {canDelete && <Button size="sm" variant="ghost" onClick={()=>remove(a)} data-testid={`delete-announcement-${a.id}`} className="text-rose-400 hover:bg-rose-950/30"><Trash2 className="w-4 h-4" /></Button>}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
